/**
 * Browser notifications for the community.
 *
 * A tiny service worker (`/sw.js`) does the heavy lifting: `showNotification`
 * keeps an alert on the user's screen (and in the system notification centre)
 * even **after the tab has been closed**, and clicking it focuses the site — or
 * opens it — directly on the article. Without a service worker we fall back to
 * `new Notification(...)`, which older desktops still understand.
 *
 * True “push while the browser is closed” needs a push server (Web Push +
 * VAPID). What we can do without one — and what this module does — is:
 *   · show the alert the moment it arrives, even in a background tab,
 *   · leave it on screen after the tab closes,
 *   · and tell the member everything that piled up the next time they open the
 *     site (see `CommunityContext`), so nothing is missed.
 */
const ICON = `${process.env.PUBLIC_URL || ''}/ckl.png`;

let registration = null;

/** Is the Notifications API available in this browser? */
export const alertsSupported = () =>
  typeof window !== 'undefined' && 'Notification' in window;

/** Did the member allow notifications? */
export const alertsAllowed = () =>
  alertsSupported() && window.Notification.permission === 'granted';

/** Register the service worker once — safe to call as often as you like. */
export async function registerAlertsWorker() {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) return null;
  if (registration) return registration;
  try {
    registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    return registration;
  } catch (e) {
    return null;
  }
}

/**
 * Show one alert. Silently does nothing when the member never allowed them.
 * @param {{title:string, body?:string, url?:string, tag?:string}} alert
 * @returns {Promise<boolean>} true when the browser accepted the alert
 */
export async function showBrowserAlert({ title, body = '', url = '/', tag = 'ckl-community' }) {
  if (!alertsAllowed()) return false;
  const options = { body, icon: ICON, badge: ICON, tag, renotify: true, data: { url } };

  try {
    const reg = await registerAlertsWorker();
    if (reg && reg.showNotification) {
      await reg.showNotification(title, options);
      return true;
    }
  } catch (e) { /* fall through to the constructor below */ }

  try {
    const note = new window.Notification(title, options);
    note.onclick = () => {
      try {
        window.focus();
        if (url && url !== '#') window.location.href = url;
        note.close();
      } catch (e) { /* ignore */ }
    };
    return true;
  } catch (e) {
    return false;      // e.g. Android Chrome without a service worker
  }
}
